import type {FormEnvironment, DataFormats, PanelDescriptor, NetworkResponse, CrmCredentials} from "@fd/Types"
import { buildNetLib } from "./NetLib.js";
import type {Categories} from "@t/list/Category"
import type { FormList } from "@t/fd/layout/FormDefinition";
import type { SimplifiedGroup } from "@t/user/SimplifiedGroup";
import type { SimplifiedUser } from "@t/user/SimplifiedUser";
import type { UsersGroups } from "@t/user/UsersGroups";
import {AppContextMap, toQueryString} from "@t/fd/AppContext";
import { EaType } from "@t/customer/EAddress";
import {SourceType, SrcType} from "@t/meta/SourceType";
import {openNewMailWindow} from "../intouch";
import {showActionDialog} from "../cti/cti_callDialog";
import {msg} from "@/i18n/Message";
import {UserInfo} from "@t/user/UserInfo";
import type {PartyName} from "@t/customer/PartyName.ts";
import {linkToPreviewKey, loadPreviewData} from "@/utils/WebPreview";
import {go} from "@/router/Router.ts";

const FORMATS:DataFormats = {
    dateFormat: new Intl.DateTimeFormat(undefined, {
        day: "numeric",
        month: "numeric",
        year: "2-digit",
        weekday: "short",
    }),
    dateTimeFormat: new Intl.DateTimeFormat(undefined, {
        day: "numeric",
        month: "numeric",
        year: "2-digit",
        hour: "numeric",
        minute: "numeric",
        weekday: "short",
    }),
    dayNameFormat: new Intl.DateTimeFormat(undefined, {
        weekday: "long",
    }),
    hourMinFormat: new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "numeric",
    }),
    dayMonthFormat: new Intl.DateTimeFormat(undefined, {
        day: "numeric",
        month: "numeric",
    }),
    // @ts-ignore
    date: window.Server.date,
    // @ts-ignore
    datetime: window.Server.datetime
};

if (typeof FORMATS.date.regex === "string") FORMATS.date.regex = new RegExp(FORMATS.date.regex);
if (typeof FORMATS.datetime.regex === "string") FORMATS.datetime.regex = new RegExp(FORMATS.datetime.regex);

function showPanel(pg: PanelDescriptor, placement?: HTMLElement | null, context?:AppContextMap): Promise<any> {
    return Promise.resolve("Not yet");
}

function findSourceType(types:SourceType[], srcType: number, srcSubType?: number):SourceType|null {
	if (typeof srcSubType === "number") {
		for (const type of types) {
			if (type.id === srcType && type.subId === srcSubType) return type;
		}
	}
	for (const type of types) {
		if (type.id === srcType) return type;
	}
	return null;
}

function replaceEditUrl(editUrl:string, srcId: number | string, srcSubType?: number) {
	let url = editUrl.replace("{id}", srcId.toString());
	url = url.replace("{subType}", (srcSubType ?? 0).toString());
	return url;
}

async function navigateTo(env:FormEnvironment, srcType: number, srcId: number | string, srcSubType?: number | undefined, fallbackUrl?: string):Promise<void> {
	const url = await urlTo(env, srcType, srcId, srcSubType);
	if (url) go(url);
	else {
		if (fallbackUrl) go(fallbackUrl);
		else console.error("Navigation to srcType "+srcType+" failed.");
	}
}

async function urlTo(env:FormEnvironment, srcType: number, srcId: number | string, srcSubType: number | undefined):Promise<string|null> {
    let types = await env.sourceTypes();
	const type = findSourceType(types, srcType, srcSubType);
	if (type?.editUrl) return replaceEditUrl(type.editUrl, srcId, srcSubType);

	let url = null;
    switch (srcType) {
        case SrcType.TASK:
            url = "editTask.do?id="+srcId;
            break;
        case SrcType.EVENT:
            url = "editEvent.do?id="+srcId;
            break;
        case SrcType.EMAIL:
            url = "readMailMessage.do?id="+srcId;
            break;
        case SrcType.OPPORTUNITY:
            url = "editOpportunity.do?id="+srcId;
            break;
        case SrcType.BUSCASE:
            url = "editBusCaseLow.do?id="+srcId;
			if (typeof srcSubType === 'number') url = url + "&caseType="+srcSubType;
            break;
        case SrcType.CONTACT:
            url = "editContact.do?id="+srcId;
            break;
        case SrcType.BRANCH:
        case SrcType.PARTY:
            url = "editParty.do?id="+srcId;
            break;
        case SrcType.PROBLEM:
            url = "editProblem.do?id="+srcId;
            break;
        case SrcType.USER:
            url = "editUser.do?id="+srcId;
            break;
        case SrcType.INVOICE:
            url = "editInvoice.do?id="+srcId;
            break;
        case SrcType.PRODUCT:
            url = "editProduct.do?id="+srcId;
            break;
        case SrcType.CONTENT:
            url = "editContent.do?id="+srcId;
            break;
        case SrcType.OFFER:
            url = "editOffer.do?id="+srcId;
            break;
        case SrcType.TEMPLATE:
            url = "editTemplate.do?id="+srcId;
            break;
        case SrcType.STORE:
            url = "editStore.do?id="+srcId;
            break;
        case SrcType.MOTION:
            url = "editMotion.do?id="+srcId;
            break;
        case SrcType.SMS:
            url = "editTemplate.do?id="+srcId;
            break;
        case SrcType.GIFT:
            break;
        case SrcType.CAMPAIGN:
            url = "editCampaign.do?id="+srcId;
            break;
        case SrcType.BUSCASE_ITEM:
            url = "editBusCaseItem.do?id="+srcId;
            break;
        case SrcType.OFFER_ITEM:
            url = "editOfferItem.do?id="+srcId;
            break;
        case SrcType.TRANSFER:
            url = "editTransfer.do?id="+srcId;
            break;
        case SrcType.ATTFILE:
            url = "editAttFile.do?id="+srcId;
            break;
        case SrcType.REPEATED_EVENT:
            url = "editEvent.do?id="+srcId;
            break;
        case SrcType.ATTFILE_ATTACH:
            break;
        case SrcType.KNOWLEDGE:
            url = "editKnowledge.do?id="+srcId;
            break;
        case SrcType.BULK_EMAIL:
            url = "editBulkMail.do?id="+srcId;
            break;
        case SrcType.RELATION:
            url = "editRelation.do?id="+srcId;
            break;
        case SrcType.CONSENT:
            url = "editConsent.do?id="+srcId;
            break;
        case SrcType.FACT:
            url = "editFact.do?id="+srcId;
            break;
        case SrcType.WORKSHEET:
            url = "editWorkSheet.do?id="+srcId;
            break;
    }
	return url;
}


function extractJson(response: NetworkResponse):Promise<any> {
    if (response.ok) {
        if (response.json !== undefined) return response.json();
        return Promise.resolve("");
    }
    return Promise.reject(response);
}

/**
 * Use this call to invoke call/messaging or email UI
 * @returns true if the link was successfully followed (false = nothing happened)
 **/
function actionLink(eaType: number, value: string, source?: HTMLElement, context?:AppContextMap):boolean {
    if (eaType === EaType.PHONE) {
        if (window.Server.hasCTI || window.Server.hasTexting) {
            showActionDialog(source, context ? toQueryString(context) : "", null, value);
            return true;
        }
    } else if (eaType === EaType.EMAIL) {
        if (window.Server.hasMail) {
            const ctx = context ? "&" + toQueryString(context) : "";
            openNewMailWindow('?recipient='+value+ctx);
            return true;
        }
    }
    return false;
}


export interface DesktopSupport {
	env: FormEnvironment;
	getJson: (endpoint: string) => Promise<any>;
	postJson: (endpoint: string, body: any) => Promise<any>;
	putJson: (endpoint: string, body: any) => Promise<any>;
	patchJson: (endpoint: string, body: any) => Promise<any>;
}


export function useDesktop(crm:CrmCredentials, user:UserInfo|null):DesktopSupport {

	const netlib = buildNetLib(crm);
	let ug:Promise<UsersGroups> = null;
	let types:Promise<SourceType[]> = null;


	function getJson(endpoint: string): Promise<any> {
		return netlib.fetch(endpoint, "GET").then(extractJson);
	}

	function postJson(endpoint: string, body: any): Promise<any> {
		return netlib.fetch(endpoint, "POST", body).then(extractJson);
	}

	function putJson(endpoint: string, body: any): Promise<any> {
		return netlib.fetch(endpoint, "PUT", body).then(extractJson);
	}

	function patchJson(endpoint: string, body: any): Promise<any> {
		return netlib.fetch(endpoint, "PATCH", body).then(extractJson);
	}

	async function loadList(id:string):Promise<FormList> {
		return getJson("api/lists/"+encodeURIComponent(id))
			.then((nr:any) => {
				let list:FormList = [];
				for (let item of nr.items) {
					if (item.enabled) list.push({
						name: item.name,
						value: item.value
					});
				}
				return list;
			});
	}

	async function loadCategories(id:string):Promise<Categories> {
		return [];
	}

	function usersGroups():Promise<UsersGroups> {
		if (ug == null) {
			ug = getJson("api/users/groups");
		}
		return ug;
	}

	function sourceTypes():Promise<SourceType[]> {
		if (types == null) {
			types = getJson("api/types");
		}
		return types;
	}

	function loadUsers():Promise<SimplifiedUser[]> {
		return usersGroups().then((ug:UsersGroups) => ug.users);
	}

	function loadGroups():Promise<SimplifiedGroup[]> {
		return usersGroups().then((ug:UsersGroups) => ug.groups);
	}

	function findPartyNames(ids: (number|string)[]): Promise<PartyName[]> {
		if (ids.length > 0) {
			const joinedIds = ids.join(",");
			let ep = "api/parties/name";
			ep = netlib.urlParam(ep, "id", joinedIds);
			return getJson(ep);
		}
		return Promise.resolve([]);
	}


	/** Return HTML code representing given object */
	function webPreview(key: string, url?:string):Promise<string> {
		if (!key || key === "href") key = linkToPreviewKey(url);
		return loadPreviewData(key);
	}

	const e:FormEnvironment = {
		touch: false,
		net: netlib,
		msg,
		showPanel: showPanel,
		urlTo: null,
		navigateTo: null,
		users: loadUsers,
		groups: loadGroups,
		parties: findPartyNames,
		user: null, // TODO not a valid user
		loadList: loadList,
		loadCategories: loadCategories,
		formats: FORMATS,
		actionLink,
		webPreview,
		sourceTypes
	};
	e.navigateTo = (srcType: number, id: number|string, srcSubType?: number):Promise<void> => {
		return navigateTo(e, srcType, id, srcSubType);
	}
	e.urlTo = (srcType: number, id: number|string, srcSubType?: number):Promise<string|null> => {
		return urlTo(e, srcType, id, srcSubType);
	}

    e.user = user;
    return {
		env: e,
		getJson,
		postJson,
		putJson,
		patchJson
	};
}
